/**
 * フロントエンド用のエラークラス定義
 */

import { t } from "lib/i18n";
export class AppError extends Error {
  constructor(
    message: string,
    public code: string,
    public statusCode: number = 500,
    public isOperational: boolean = true
  ) {
    super(message);
    this.name = 'AppError';
    Object.setPrototypeOf(this, AppError.prototype);
  }
}

export class ValidationError extends AppError {
  constructor(message: string, public fields: Record<string, string>) {
    super(message, 'VALIDATION_ERROR', 400);
    this.name = 'ValidationError';
  }
}

export class NetworkError extends AppError {
  constructor(message: string, public retryable: boolean = true) {
    super(message, 'NETWORK_ERROR', 0);
    this.name = 'NetworkError';
  }
}

export class AuthenticationError extends AppError {
  constructor(message = 'Authentication failed') {
    super(message, 'AUTHENTICATION_ERROR', 401);
    this.name = 'AuthenticationError';
  }
}

export class FileUploadError extends AppError {
  constructor(message: string, public fileType?: string, public fileSize?: number) {
    super(message, 'FILE_UPLOAD_ERROR', 400);
    this.name = 'FileUploadError';
  }
}

export class GraphQLError extends AppError {
  constructor(message: string, public operationName: string) {
    super(message, 'GRAPHQL_ERROR', 400);
    this.name = 'GraphQLError';
  }
}

/**
 * 型安全なエラーアサーション
 */
export function assertError(error: unknown): asserts error is Error {
  if (!(error instanceof Error)) {
    throw new Error(`Expected Error instance, got ${typeof error}`);
  }
}

/**
 * エラーが特定のタイプかどうかをチェック
 */
export function isAppError(error: unknown): error is AppError {
  return error instanceof AppError;
}

export function isValidationError(error: unknown): error is ValidationError {
  return error instanceof ValidationError;
}

export function isNetworkError(error: unknown): error is NetworkError {
  return error instanceof NetworkError;
}

export function isAuthenticationError(error: unknown): error is AuthenticationError {
  return error instanceof AuthenticationError;
}

export function isFileUploadError(error: unknown): error is FileUploadError {
  return error instanceof FileUploadError;
}

export function isGraphQLError(error: unknown): error is GraphQLError {
  return error instanceof GraphQLError;
}

/**
 * エラーレベルの判定
 */
export function getErrorLevel(error: unknown): 'error' | 'warning' | 'info' {
  if (isAuthenticationError(error)) {
    return 'error';
  }
  
  if (isNetworkError(error)) {
    return error.retryable ? 'warning' : 'error';
  }
  
  if (isValidationError(error)) {
    return 'warning';
  }
  
  if (isFileUploadError(error)) {
    return 'warning';
  }
  
  // デフォルトはエラー
  return 'error';
}

/**
 * ユーザーフレンドリーなエラーメッセージの生成
 */
export function getUserFriendlyMessage(error: unknown): string {
  if (isValidationError(error)) {
    const fieldNames = Object.keys(error.fields).join(', ');
    return t('There is a problem with your input: {{fields}}', { fields: fieldNames });
  }

  if (isNetworkError(error)) {
    return t('A network error occurred. Please wait a moment and try again.');
  }

  if (isAuthenticationError(error)) {
    return t('Please sign in again.');
  }

  if (isFileUploadError(error)) {
    return t('Failed to upload the file. Please check the file type and size.');
  }

  if (isGraphQLError(error)) {
    return t('An error occurred while processing. Please wait a moment and try again.');
  }

  return t('An error occurred. Please wait a moment and try again.');
}